// Chakra imports
import { Flex, FormLabel, Text, Box, useColorModeValue, useToken } from '@chakra-ui/react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

interface SelectOption {
  value: any;
  label: string;
}

export default function SelectField(props: {
  id?: string;
  label?: string;
  extra?: JSX.Element;
  placeholder?: string;
  options?: SelectOption[];
  value?: any;
  onChange?: (value: any, option: SelectOption | null) => void;
  required?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  isSearchable?: boolean;
  isLoading?: boolean;
  mb?: string;
  creatable?: boolean;
  onCreateOption?: (inputValue: string) => void;
  formatCreateLabel?: (inputValue: string) => string;
  [x: string]: any;
}) {
  const {
    id,
    label,
    extra,
    placeholder,
    options = [],
    value,
    onChange,
    mb,
    required,
    isInvalid,
    isDisabled,
    isSearchable = true,
    isLoading,
    creatable,
    onCreateOption,
    formatCreateLabel,
    ...rest
  } = props;

  const textColorPrimary = useColorModeValue('gray.800', 'whiteAlpha.900');
  const textColorSecondary = useColorModeValue('gray.500', 'gray.400');

  const bgPrimaryTokenName = useColorModeValue('gray.50', 'gray.700');
  const borderColorTokenName = useColorModeValue('gray.200', 'gray.600');
  const hoverBorderColorTokenName = useColorModeValue('gray.300', 'gray.500');
  const focusBorderColorTokenName = useColorModeValue('blue.400', 'blue.300');
  const invalidBorderColorTokenName = useColorModeValue('red.400', 'red.300');
  const textColorTokenName = useColorModeValue('gray.800', 'whiteAlpha.900');
  const placeholderColorTokenName = useColorModeValue('gray.500', 'gray.400');
  const menuBgTokenName = useColorModeValue('white', 'gray.700');
  const optionHoverBgTokenName = useColorModeValue('gray.100', 'gray.600');
  const optionSelectedBgTokenName = useColorModeValue('blue.400', 'blue.300');

  const [
    bgPrimary,
    borderColorToken,
    hoverBorderColorToken,
    focusBorderColorToken,
    invalidBorderColorToken,
    textColorToken,
    placeholderColorToken,
    menuBg,
    optionHoverBg,
    optionSelectedBg,
  ] = useToken('colors', [
    bgPrimaryTokenName,
    borderColorTokenName,
    hoverBorderColorTokenName,
    focusBorderColorTokenName,
    invalidBorderColorTokenName,
    textColorTokenName,
    placeholderColorTokenName,
    menuBgTokenName,
    optionHoverBgTokenName,
    optionSelectedBgTokenName,
  ]);

  const selectedOption = options.find((option) => option.value === value) ?? null;

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: '40px',
      borderRadius: '10px',
      borderWidth: '1px',
      borderColor: isInvalid
        ? invalidBorderColorToken
        : state.isFocused
        ? focusBorderColorToken
        : borderColorToken,
      backgroundColor: bgPrimary,
      boxShadow: state.isFocused
        ? `0 0 0 2px ${isInvalid ? invalidBorderColorToken : focusBorderColorToken}`
        : 'none',
      opacity: isDisabled ? 0.6 : 1,
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: isInvalid ? invalidBorderColorToken : hoverBorderColorToken,
      },
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: '2px 12px',
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: textColorToken,
      fontSize: '14px',
      fontWeight: 500,
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: placeholderColorToken,
      fontSize: '14px',
    }),
    input: (provided: any) => ({
      ...provided,
      color: textColorToken,
      fontSize: '14px',
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: menuBg,
      borderRadius: '10px',
      overflow: 'hidden',
      zIndex: 10,
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      fontSize: '14px',
      backgroundColor: state.isSelected
        ? optionSelectedBg
        : state.isFocused
        ? optionHoverBg
        : 'transparent',
      color: state.isSelected ? 'white' : textColorToken,
      cursor: 'pointer',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
  };

  return (
    <Flex direction="column" mb={mb ?? '24px'} width="100%">
      {label && (
        <FormLabel
          htmlFor={id}
          fontSize="sm"
          color={textColorPrimary}
          fontWeight="semibold"
          mb="6px"
        >
          {label}
          {required && <Text as="span" color="red.400" ml="1">*</Text>}
          {extra && (
            <Text fontSize="xs" ml="2" color={textColorSecondary} fontWeight="normal">
              {extra}
            </Text>
          )}
        </FormLabel>
      )}

      <Box position="relative">
        {creatable ? (
          <CreatableSelect
            {...rest}
            inputId={id}
            placeholder={placeholder}
            options={options}
            value={selectedOption}
            onChange={(option: SelectOption | null) => onChange?.(option ? option.value : '', option)}
            onCreateOption={onCreateOption}
            formatCreateLabel={formatCreateLabel ?? ((inputValue: string) => `Crear "${inputValue}"`)}
            isDisabled={isDisabled}
            isLoading={isLoading}
            isSearchable={isSearchable}
            styles={customStyles}
          />
        ) : (
          <Select
            {...rest}
            inputId={id}
            placeholder={placeholder}
            options={options}
            value={selectedOption}
            onChange={(option: SelectOption | null) => onChange?.(option ? option.value : '', option)}
            isDisabled={isDisabled}
            isSearchable={isSearchable}
            styles={customStyles}
          />
        )}
      </Box>
    </Flex>
  );
}
